import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useToast, useDisclosure } from "@chakra-ui/react";
import { api, API_BASE, logout, setSession } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { APPRECIATION_TYPES } from "./appreciations";
import AppreciationModal from "./AppreciationModal";
import EfficiencyPanel from "./EfficiencyPanel";

const PRESENCE_LABELS = {
  active: "Active", busy: "Busy", in_meeting: "In Meeting",
  dnd: "Do Not Disturb", offline: "Offline",
};
const PRESENCE_COLORS = {
  active: "#22c55e", busy: "#f59e0b", in_meeting: "#a855f7",
  dnd: "#ef4444", offline: "#94a3b8",
};
const PRESENCE_KEYS = ["active", "busy", "in_meeting", "dnd", "offline"];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ProfileView({ mode }) {
  // mode: "self" or "other"
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const meId = localStorage.getItem("userID");
  const isSelf = mode === "self" || (params.id && String(params.id) === String(meId));
  const userId = isSelf ? meId : params.id;

  const [user, setUser] = useState(null);
  const [appreciations, setAppreciations] = useState([]);
  const [appreciationCounts, setAppreciationCounts] = useState({});
  const [tab, setTab] = useState("about");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingAvatar, setSavingAvatar] = useState(false);
  const avatarRef = useRef(null);
  const apprModal = useDisclosure();
  const [presenceOpen, setPresenceOpen] = useState(false);

  useShellHeader({ title: isSelf ? "My Profile" : (user?.username || "Profile") }, [user?.username, isSelf]);

  async function load() {
    setLoading(true);
    try {
      const endpoint = isSelf ? "/me" : `/users/${userId}/profile`;
      const [me, appr] = await Promise.all([
        api(endpoint),
        api(`/appreciations/by-user/${userId}`).catch(() => ({ appreciations: [], counts: {} })),
      ]);
      setUser(me.user);
      setAppreciations(appr.appreciations || []);
      setAppreciationCounts(appr.counts || {});
      setForm({
        username: me.user.username || "",
        designation: me.user.designation || "",
        phone: me.user.phone || "",
        bio: me.user.bio || "",
        location: me.user.location || "",
        skills: (me.user.skills || []).join(", "),
        certifications: (me.user.certifications || []).join("\n"),
        joiningDate: me.user.joiningDate ? me.user.joiningDate.slice(0, 10) : "",
        socialLinks: {
          linkedin: me.user.socialLinks?.linkedin || "",
          website: me.user.socialLinks?.website || "",
          portfolio: me.user.socialLinks?.portfolio || "",
          twitter: me.user.socialLinks?.twitter || "",
        },
      });
    } catch (e) {
      if (e.status === 401) { logout(); navigate("/login"); return; }
      toast({ title: e.message, status: "error" });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  async function saveProfile() {
    try {
      await api("/me", {
        method: "PATCH",
        body: JSON.stringify({
          username: form.username,
          designation: form.designation,
          phone: form.phone,
          bio: form.bio,
          location: form.location,
          skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
          certifications: form.certifications.split("\n").map((s) => s.trim()).filter(Boolean),
          joiningDate: form.joiningDate || null,
          socialLinks: form.socialLinks,
        }),
      });
      if (form.username) localStorage.setItem("username", form.username);
      toast({ title: "Profile updated", status: "success" });
      setEditing(false);
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  async function uploadAvatar(file) {
    if (!file) return;
    setSavingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_BASE}/me/avatar`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Upload failed");
      toast({ title: "Avatar updated", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setSavingAvatar(false); }
  }

  async function setPresence(status) {
    try {
      await api("/me/presence", { method: "PATCH", body: JSON.stringify({ presenceStatus: status }) });
      setUser((u) => ({ ...u, presenceStatus: status }));
      setPresenceOpen(false);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  if (loading) return <div className="text-sm text-ink-400">Loading...</div>;
  if (!user) return null;

  const avatarSrc = user.profileImage
    ? (user.profileImage.startsWith("http") ? user.profileImage : `${user.profileImage}`)
    : null;
  const initials = (user.username || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const presence = user.presenceStatus || "active";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">

      {/* LEFT — sidebar */}
      <aside className="bg-white rounded-md shadow-sm p-5 self-start">
        <div className="relative w-32 h-32 mx-auto mb-3">
          <div
            className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white text-4xl font-semibold flex items-center justify-center overflow-hidden"
            style={avatarSrc ? { background: `url(${avatarSrc}) center/cover no-repeat` } : {}}
          >
            {!avatarSrc && initials}
          </div>
          <span
            className="absolute bottom-2 right-2 w-5 h-5 rounded-full border-2 border-white"
            style={{ background: PRESENCE_COLORS[presence] }}
            title={PRESENCE_LABELS[presence]}
          />
          {isSelf && (
            <>
              <button
                onClick={() => avatarRef.current?.click()}
                disabled={savingAvatar}
                className="absolute -bottom-1 left-2 px-2 py-0.5 text-[10px] bg-white border border-ink-200 rounded-full shadow-sm hover:bg-ink-50"
              >
                {savingAvatar ? "Uploading..." : "Change"}
              </button>
              <input
                ref={avatarRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { uploadAvatar(e.target.files?.[0]); e.target.value = ""; }}
              />
            </>
          )}
        </div>

        <div className="text-center">
          <div className="text-lg font-semibold text-ink-800">{user.username}</div>
          <div className="text-xs text-ink-500">{user.designation || "—"}</div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5 justify-center">
          <span className={[
            "px-2 py-0.5 text-[10px] rounded-full font-semibold",
            user.role === "SUPER_ADMIN" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700",
          ].join(" ")}>{user.role || "TEAM_USER"}</span>
          {user.team && (
            <span className="px-2 py-0.5 text-[10px] rounded-full text-white" style={{ background: user.team.color || "#94a3b8" }}>
              {user.team.name}
            </span>
          )}
        </div>

        {/* Presence */}
        <div className="mt-4">
          {isSelf ? (
            <div className="relative">
              <button
                onClick={() => setPresenceOpen((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-1.5 border border-ink-200 rounded-md text-sm hover:bg-ink-50"
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: PRESENCE_COLORS[presence] }} />
                  <span className="text-ink-700">{PRESENCE_LABELS[presence]}</span>
                </span>
                <span className="text-xs text-ink-400">change</span>
              </button>
              {presenceOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-ink-200 rounded-md shadow-md py-1">
                  {PRESENCE_KEYS.map((k) => (
                    <button
                      key={k}
                      onClick={() => setPresence(k)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-ink-50 text-left"
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: PRESENCE_COLORS[k] }} />
                      <span>{PRESENCE_LABELS[k]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 border border-ink-200 rounded-md text-sm">
              <span className="w-2 h-2 rounded-full" style={{ background: PRESENCE_COLORS[presence] }} />
              <span className="text-ink-700">{PRESENCE_LABELS[presence]}</span>
              {user.lastSeen && <span className="ml-auto text-[10px] text-ink-400">last: {timeAgo(user.lastSeen)}</span>}
            </div>
          )}
        </div>

        {!isSelf && (
          <button
            onClick={apprModal.onOpen}
            className="w-full mt-3 px-3 py-2 bg-gradient-to-r from-amber-400 to-pink-500 text-white text-sm font-semibold rounded-md hover:opacity-90"
          >
            🌟 Appreciate
          </button>
        )}

        {isSelf && (
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full mt-3 px-3 py-1.5 border border-ink-200 rounded-md text-sm text-ink-600 hover:bg-ink-50"
          >
            Sign out
          </button>
        )}

        {/* Quick info block — WP parity: profile portal showed employeeCode,
            department, designation, shift, joining date, employment type,
            emergency contact. Sensitive fields (salary, PAN, Aadhaar, bank)
            only render for self (isSelf). */}
        <div className="mt-5 pt-5 border-t border-ink-100 space-y-2 text-xs">
          <Row label="Email">{user.email}</Row>
          {user.employeeCode && <Row label="Employee code">{user.employeeCode}</Row>}
          {user.phone && <Row label="Phone">{user.phone}</Row>}
          {user.team?.name && <Row label="Department">{user.team.name}</Row>}
          {(user.designationRef?.name || user.designation) && (
            <Row label="Designation">{user.designationRef?.name || user.designation}</Row>
          )}
          {user.shift && (
            <Row label="Shift">{user.shift.name} ({user.shift.startTime}–{user.shift.endTime})</Row>
          )}
          {user.employmentType && (
            <Row label="Employment">{String(user.employmentType).replace("_", " ")}</Row>
          )}
          {user.location && <Row label="Location">{user.location}</Row>}
          {user.joiningDate && <Row label="Joined">{new Date(user.joiningDate).toLocaleDateString()}</Row>}
          {user.reportingTo && (
            <Row label="Reports to">
              <RouterLink to={`/users/${user.reportingTo._id}`} className="text-blue-600 hover:underline">{user.reportingTo.username}</RouterLink>
            </Row>
          )}
          {isSelf && user.salary > 0 && (
            <Row label="Salary">₹{Number(user.salary).toLocaleString("en-IN")}</Row>
          )}
          {isSelf && user.bloodGroup && <Row label="Blood group">{user.bloodGroup}</Row>}
          {isSelf && user.emergencyContact?.name && (
            <Row label="Emergency">
              {user.emergencyContact.name}
              {user.emergencyContact.relation ? ` (${user.emergencyContact.relation})` : ""}
              {user.emergencyContact.phone ? ` — ${user.emergencyContact.phone}` : ""}
            </Row>
          )}
          <Row label="MFA">{user.mfaEnabled ? "✅ Enabled" : "—"}</Row>
        </div>
      </aside>

      {/* RIGHT — content */}
      <main>
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-ink-200">
          {[
            { id: "about", label: "About" },
            { id: "hierarchy", label: "Hierarchy" },
            { id: "appreciations", label: `Appreciations (${appreciations.length})` },
            { id: "performance", label: "Performance" },
            ...(isSelf ? [{ id: "security", label: "Security" }] : []),
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={[
                "px-3 py-2 text-sm border-b-2 -mb-px transition",
                tab === t.id ? "border-blue-500 text-blue-600 font-semibold" : "border-transparent text-ink-500 hover:text-ink-700",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
          <div className="ml-auto">
            {isSelf && tab === "about" && !editing && (
              <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-sm bg-ink-100 rounded-md hover:bg-ink-200">Edit profile</button>
            )}
            {isSelf && tab === "about" && editing && (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm rounded-md hover:bg-ink-100">Cancel</button>
                <button onClick={saveProfile} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600">Save</button>
              </div>
            )}
          </div>
        </div>

        {/* Tab content */}
        {tab === "about" && (
          <AboutTab user={user} editing={editing && isSelf} form={form} setForm={setForm} />
        )}
        {tab === "hierarchy" && (
          <HierarchyTab user={user} />
        )}
        {tab === "appreciations" && (
          <AppreciationsTab appreciations={appreciations} counts={appreciationCounts} />
        )}
        {tab === "performance" && (
          <EfficiencyPanel
            userId={user._id}
            isSelf={isSelf}
            isAdmin={localStorage.getItem("role") === "SUPER_ADMIN"}
          />
        )}
        {tab === "security" && isSelf && <SecurityTab />}
      </main>

      {!isSelf && (
        <AppreciationModal
          isOpen={apprModal.isOpen}
          onClose={apprModal.onClose}
          recipient={user}
          onGiven={() => load()}
        />
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-ink-500">{label}</span>
      <span className="text-ink-700 text-right">{children}</span>
    </div>
  );
}

function AboutTab({ user, editing, form, setForm }) {
  if (editing) {
    return (
      <div className="bg-white rounded-md shadow-sm p-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Username"><input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input" /></Field>
          <Field label="Designation"><input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="input" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></Field>
          <Field label="Location"><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input" /></Field>
          <Field label="Joining date"><input type="date" value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} className="input" /></Field>
          <Field label="Skills (comma-separated)"><input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="input" /></Field>
        </div>
        <Field label="Bio">
          <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={4} className="input" />
        </Field>
        <Field label="Certifications (one per line)">
          <textarea value={form.certifications} onChange={(e) => setForm({ ...form, certifications: e.target.value })} rows={3} className="input" />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="LinkedIn"><input value={form.socialLinks.linkedin} onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, linkedin: e.target.value } })} placeholder="https://linkedin.com/in/..." className="input" /></Field>
          <Field label="Website"><input value={form.socialLinks.website} onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, website: e.target.value } })} placeholder="https://" className="input" /></Field>
          <Field label="Portfolio"><input value={form.socialLinks.portfolio} onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, portfolio: e.target.value } })} placeholder="https://" className="input" /></Field>
          <Field label="Twitter"><input value={form.socialLinks.twitter} onChange={(e) => setForm({ ...form, socialLinks: { ...form.socialLinks, twitter: e.target.value } })} placeholder="https://twitter.com/..." className="input" /></Field>
        </div>
        <style>{`.input{width:100%;font-size:14px;padding:6px 10px;border:1px solid var(--border-default);border-radius:6px;outline:none;background:var(--bg-surface);color:var(--text-primary)}.input:focus{border-color:var(--accent-primary)}`}</style>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-md shadow-sm p-5">
        <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">About</h3>
        {user.bio ? (
          <p className="text-sm text-ink-700 whitespace-pre-wrap">{user.bio}</p>
        ) : <p className="text-sm text-ink-400 italic">No bio yet.</p>}
      </div>

      {(user.skills || []).length > 0 && (
        <div className="bg-white rounded-md shadow-sm p-5">
          <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">Skills</h3>
          <div className="flex flex-wrap gap-1.5">
            {user.skills.map((s) => (
              <span key={s} className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">{s}</span>
            ))}
          </div>
        </div>
      )}

      {(user.certifications || []).length > 0 && (
        <div className="bg-white rounded-md shadow-sm p-5">
          <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">Certifications</h3>
          <ul className="space-y-1 list-disc list-inside text-sm text-ink-700">
            {user.certifications.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {user.socialLinks && Object.values(user.socialLinks).some(Boolean) && (
        <div className="bg-white rounded-md shadow-sm p-5">
          <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">Links</h3>
          <ul className="space-y-1 text-sm">
            {user.socialLinks.linkedin && <li><a href={user.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">LinkedIn</a></li>}
            {user.socialLinks.website && <li><a href={user.socialLinks.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website</a></li>}
            {user.socialLinks.portfolio && <li><a href={user.socialLinks.portfolio} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Portfolio</a></li>}
            {user.socialLinks.twitter && <li><a href={user.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Twitter</a></li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function HierarchyTab({ user }) {
  return (
    <div className="bg-white rounded-md shadow-sm p-5">
      <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-3">Reporting hierarchy</h3>
      {user.reportingTo ? (
        <div className="mb-4">
          <div className="text-xs text-ink-500 mb-1">Reports to</div>
          <RouterLink to={`/users/${user.reportingTo._id}`} className="inline-flex items-center gap-2 px-2 py-1.5 bg-ink-50 hover:bg-ink-100 rounded-md text-sm">
            <span className="w-7 h-7 rounded-full bg-ink-300 text-white text-xs flex items-center justify-center">{(user.reportingTo.username || "?").charAt(0).toUpperCase()}</span>
            <span>{user.reportingTo.username}</span>
            <span className="text-xs text-ink-500">{user.reportingTo.designation || user.reportingTo.email}</span>
          </RouterLink>
        </div>
      ) : <div className="text-sm text-ink-400 italic mb-4">No manager assigned.</div>}

      <div>
        <div className="text-xs text-ink-500 mb-2">Direct reports ({(user.directReports || []).length})</div>
        {(user.directReports || []).length === 0 ? (
          <div className="text-sm text-ink-400 italic">No direct reports.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {user.directReports.map((r) => (
              <RouterLink key={r._id} to={`/users/${r._id}`} className="flex items-center gap-2 p-2 bg-ink-50 hover:bg-ink-100 rounded-md text-sm">
                <span className="w-7 h-7 rounded-full bg-ink-300 text-white text-xs flex items-center justify-center">{(r.username || "?").charAt(0).toUpperCase()}</span>
                <div>
                  <div className="font-medium">{r.username}</div>
                  <div className="text-xs text-ink-500">{r.designation || r.email}</div>
                </div>
              </RouterLink>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AppreciationsTab({ appreciations, counts }) {
  const total = appreciations.length;
  const sortedTypes = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-md shadow-sm p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-3xl">🌟</div>
          <div>
            <div className="text-xl font-bold">{total}</div>
            <div className="text-xs text-ink-500 uppercase tracking-wider">Total appreciations</div>
          </div>
        </div>
        {sortedTypes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {sortedTypes.map(([key, n]) => {
              const m = APPRECIATION_TYPES[key] || { label: key, emoji: "🏆" };
              return (
                <span key={key} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full" style={{ background: (m.color || "#94A3B8") + "22", color: "var(--text-primary)" }} /* tint keeps the per-type identity; ink is tokenised because the colour itself on its own 13% tint was 1.9–3.6:1 in light mode */>
                  <span>{m.emoji}</span>
                  <span>{m.label} × {n}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
      {appreciations.length === 0 ? (
        <div className="bg-white rounded-md shadow-sm p-10 text-center text-sm text-ink-400 italic">No appreciations yet — but it's never too late to start a streak.</div>
      ) : (
        <ul className="space-y-2">
          {appreciations.map((a) => {
            const m = APPRECIATION_TYPES[a.type] || { label: a.type, emoji: "🏆" };
            return (
              <li key={a._id} className="bg-white rounded-md shadow-sm p-4 flex items-start gap-3">
                <div className="text-2xl flex-shrink-0">{m.emoji}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold" style={{ color: m.color }}>{m.label}</span>
                    <span className="text-xs text-ink-400">·</span>
                    <span className="text-xs text-ink-500">from {a.from?.username || "—"}</span>
                    <span className="text-xs text-ink-400 ml-auto">{new Date(a.createdAt).toLocaleDateString()}</span>
                  </div>
                  {a.note && <div className="text-sm text-ink-700">{a.note}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PerformanceTab() {
  return (
    <div className="bg-white rounded-md shadow-sm p-10 text-center">
      <div className="text-5xl mb-3">📈</div>
      <h3 className="text-lg font-semibold text-ink-700 mb-2">Efficiency Engine — coming in Phase 12b</h3>
      <p className="text-sm text-ink-500 max-w-md mx-auto">
        A credit-score-style 0–1000 number rolling up 8 factors: completion rate, deadline discipline,
        complexity handled, approval success, consistency, workload, collaboration, appreciations.
        With department normalization and anti-gaming gates.
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-500 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

// Security tab — change own password. Posts to POST /api/me/password.
// Server bumps tokenVersion on success, which ends every session, this one
// included, so it returns a fresh token for this device. Storing it keeps the
// person signed in here; before, their next click bounced them to sign-in.
function SecurityTab() {
  const toast = useToast();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e?.preventDefault();
    if (!cur || !next) { toast({ title: "Fill both fields", status: "warning" }); return; }
    if (next.length < 8) { toast({ title: "New password must be at least 8 characters", status: "warning" }); return; }
    if (next !== confirm) { toast({ title: "New passwords don't match", status: "warning" }); return; }
    if (cur === next) { toast({ title: "New password must be different from current", status: "warning" }); return; }
    setSaving(true);
    try {
      const r = await api("/me/password", { method: "POST", body: JSON.stringify({ currentPassword: cur, newPassword: next }) });
      if (r.token) setSession({ token: r.token });
      toast({ title: r.message || "Password changed.", status: "success" });
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast({ title: e.data?.message || e.message || "Could not change password", status: "error" });
    } finally { setSaving(false); }
  }

  const inputCls = "w-full px-3 py-2 text-sm border border-ink-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-200";
  return (
    <form onSubmit={submit} className="max-w-md space-y-4">
      <div>
        <h3 className="text-base font-semibold text-ink-800 mb-1">Change password</h3>
        <p className="text-sm text-ink-500">
          If you logged in with a temporary password, please change it here. You stay signed in on this device; other devices are signed out.
          If your browser offers to update the saved password, accept it, so it stops filling in the old one.
        </p>
      </div>
      <Field label="Current password">
        <input type="password" autoComplete="current-password" className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} />
      </Field>
      <Field label="New password (min 8 characters)">
        <input type="password" autoComplete="new-password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} />
      </Field>
      <Field label="Confirm new password">
        <input type="password" autoComplete="new-password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <div className="pt-1">
        <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-60">
          {saving ? "Saving…" : "Change password"}
        </button>
      </div>
    </form>
  );
}
